/* voyado-product-recommendations.js
 * Coordinator for the product-page recommendation section: reads each child
 * block's algorithm and issues ONE elevate.query.productPage request carrying
 * all algorithms in recommendationLists, then distributes results per block by
 * list id. Rendering + slider behaviour come from window.VoyadoRec.
 */
class VoyadoProductRecommendations extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#init(this.#ac.signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #init(signal) {
    await window.voyadoElevateReady;
    if (signal.aborted) return;

    const R = window.VoyadoRec;
    if (!window.elevate?.query) {
      console.warn("[voyado-recs] window.elevate not ready — aborted", this);
      this.hidden = true;
      return;
    }
    if (!R) {
      console.warn("[voyado-recs] VoyadoRec core missing", this);
      return;
    }

    const productKey = this.dataset.productKey ?? "";
    if (!productKey) {
      // Not a product context (e.g. editor preview without a product) — hide.
      this.hidden = true;
      return;
    }

    const blocks = [...this.querySelectorAll("[data-voyado-list]")].map((el) => ({
      el,
      grid: el.querySelector("[data-voyado-grid]"),
      listId: el.dataset.listId,
      algorithm: el.dataset.algorithm || "RECENTLY_VIEWED",
    })).filter((b) => b.grid && b.listId);

    if (!blocks.length) {
      this.hidden = true;
      return;
    }

    this.#fetch(productKey, blocks, R, signal);
  }

  async #fetch(productKey, blocks, R, signal) {
    try {
      const recommendationLists = blocks.map((b) => ({ id: b.listId, algorithm: b.algorithm }));
      const data = await window.elevate.query.productPage({ productKey }, { recommendationLists });
      if (signal.aborted) return;

      R.captureVariantTickets(data);

      const lists = data.recommendationLists ?? [];
      let anyRendered = false;

      for (const b of blocks) {
        const match = lists.find((l) => l.id === b.listId);
        const groups = match?.productGroups ?? [];
        b.grid.querySelectorAll(".voyado-skeleton").forEach((el) => el.remove());

        if (!groups.length) {
          b.el.hidden = true;
          continue;
        }

        const frag = document.createDocumentFragment();
        let i = 0;
        for (const g of groups) {
          const p = g.products?.[0] ?? g;
          const ticket = g.ticket ?? p.ticket ?? "";
          const item = document.createElement("div");
          item.className = "product-series__item";
          item.setAttribute("role", "listitem");
          item.innerHTML = R.renderCard(p, ticket, i++);
          frag.appendChild(item);
        }
        b.grid.appendChild(frag);

        const slider = R.setupSlider(b.grid, {
          prev: b.el.querySelector("[data-prev]"),
          next: b.el.querySelector("[data-next]"),
          scrollbarWrap: b.el.querySelector("[data-voyado-scrollbar]"),
          scrollbarThumb: b.el.querySelector("[data-voyado-scrollbar-thumb]"),
        }, signal);
        slider.update();
        anyRendered = true;
      }

      if (!anyRendered) this.hidden = true;
    } catch (e) {
      console.error("[voyado-recs] fetch failed:", e, this);
      this.querySelectorAll(".voyado-skeleton").forEach((el) => el.remove());
      this.hidden = true;
    }
  }
}

customElements.define("voyado-product-recommendations", VoyadoProductRecommendations);
